import { SharedInterfaceVehicles } from "./sharedInterface";
import { AddVehicleModal } from "./modals/addVehicleModal";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCompany, selectManager } from "../homeSlice";
import { useTranslation } from "react-i18next";
import { AddOutlined, triggerToast, triggerToastDS, UploadFileOutlined, WifiTetheringOutlined } from "@qivia/ui";
import {
  vehiclesSlice,
  selectDeactivateVehicleStatus,
  selectMultipleVehiclesCreatedStatus,
  selectErrorMessageMassivImport,
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
  selectRequestVehiclesConnectionStatus,
} from "./vehiclesSlice";
import { VehiclesListType } from "./vehiclesAPI";
import { useNavigate } from "react-router-dom";
import {
  getDepartmentsByCompanyAsync,
  selectGetDepartmentsByCompanyStatus,
} from "../departments/departmentsSlice";
import { selectManagersList } from "../users/usersSlice";
import { VehicleSidePanel } from "./sidePanel/SidePanel";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";
import {
  driversHistoryAsync,
  selectDriversHistoryListStatus,
} from "../fleet/fleetSlice";
import { cardsSlice } from "../cardsAndBadges/cards/cardsSlice";
import { AddMultipleVehiclesRequestConnectionModal } from "./modals/addMultipleVehiclesRequestConnectionModal";

export const Vehicles = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRequestConnectionModalVisible, setIsRequestConnectionModalVisible] = useState(false);
  const deactivateVehicleStatus = useAppSelector(selectDeactivateVehicleStatus);

  const createMultipleVehiclesStatus = useAppSelector(
    selectMultipleVehiclesCreatedStatus,
  );

  const vehiclesMassiveImportErrorMessage = useAppSelector(
    selectErrorMessageMassivImport,
  );

  const manager = useAppSelector(selectManager);
  const managers = useAppSelector(selectManagersList);
  const company = useAppSelector(selectCompany);
  const getDepartmentsByCompanyStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );
  const vehiclesStatus = useAppSelector(selectListAllVehiclesInDbStatus);
  const driversHistoryListStatus = useAppSelector(
    selectDriversHistoryListStatus,
  );
  const requestVehiclesConnectionStatus = useAppSelector(
    selectRequestVehiclesConnectionStatus,
  );

  const hasCompanyBlockedOrders = useMemo(() => {
    return company?.orderBlockedAt !== null;
  }, [company]);

  const isUserManager = useMemo(() => manager?.role === "USER", [manager]);

  const canOrderVehicles = useMemo(() => {
    return !isUserManager && !hasCompanyBlockedOrders;
  }, [isUserManager, hasCompanyBlockedOrders]);

  const canRequestConnection = useMemo(() => {
    return !isUserManager && !!company?.hasAdvancedAnalytics;
  }, [isUserManager, company]);

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  useEffect(() => {
    if (company && getDepartmentsByCompanyStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(company.uuid));
    }
  }, [company, dispatch, getDepartmentsByCompanyStatus]);

  useEffect(() => {
    if (company && vehiclesStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, vehiclesStatus]);

  useEffect(() => {
    if (company && driversHistoryListStatus === "idle") {
      void dispatch(driversHistoryAsync(company.uuid));
    }
  }, [company, dispatch, driversHistoryListStatus]);

  useEffect(() => {
    if (
      createMultipleVehiclesStatus === "success" &&
      vehiclesMassiveImportErrorMessage
    ) {
      triggerToast(
        t("vehicles.modal.addMultipleVehicles.sameListError"),
        "error",
      );
      dispatch(vehiclesSlice.actions.resetCreateMultipleVehiclesStatus());
    } else if (createMultipleVehiclesStatus === "failed") {
      triggerToast(
        t("vehicles.modal.addMultipleVehicles.failure") || "",
        "error",
      );
      dispatch(vehiclesSlice.actions.resetCreateMultipleVehiclesStatus());
    }
  }, [
    createMultipleVehiclesStatus,
    dispatch,
    t,
    vehiclesMassiveImportErrorMessage,
  ]);

  useEffect(() => {
    if (requestVehiclesConnectionStatus === "success") {
      triggerToastDS(t("vehicles.requestConnection.success") || "", "SUCCESS");
      dispatch(vehiclesSlice.actions.resetRequestVehiclesConnectionStatus());
      dispatch(vehiclesSlice.actions.resetListAllVehiclesInDbStatus());
    } else if (requestVehiclesConnectionStatus === "failed") {
      triggerToastDS(t("vehicles.requestConnection.error") || "", "ERROR");
      dispatch(vehiclesSlice.actions.resetRequestVehiclesConnectionStatus());
    }
  }, [dispatch, requestVehiclesConnectionStatus, t]);

  useEffect(() => {
    if (deactivateVehicleStatus === "success") {
      triggerToast(t("vehicles.deactivated.success") || "", "valid");
      dispatch(vehiclesSlice.actions.resetDeactivateVehicleStatus());
      dispatch(cardsSlice.actions.resetCardsListStatus());
    } else if (deactivateVehicleStatus === "failed") {
      triggerToast(t("vehicles.deactivated.failure") || "", "error");
      dispatch(vehiclesSlice.actions.resetDeactivateVehicleStatus());
    }
  }, [deactivateVehicleStatus, dispatch, t]);

  const cta = {
    title: t("vehicles.cta"),
    id: "add-vehicle-button",
    action: () => {
      setIsModalVisible(true);
    },
    leftIcon: <AddOutlined />,
  };

  const cta2 = {
    title: t("vehicles.cta2"),
    id: "add-multiple-vehicles-button",
    action: () => {
      navigate("/home/vehicles_import");
    },
    leftIcon: <UploadFileOutlined />,
  };

  const cta3 = {
    title: t("vehicles.requestConnection.cta"),
    id: "request-vehicles-connection-button",
    action: () => {
      setIsRequestConnectionModalVisible(true);
    },
    leftIcon: <WifiTetheringOutlined />,
  };

  const sidePanelAction = useCallback(
    (props: Partial<VehiclesListType>) => {
      navigate(`/home/vehicles/${props.uuid}`);
    },
    [navigate],
  );

  const reloadListWithDateFilters = useCallback(
    (dates: Date[]) => {
      if (!company) return;
      void dispatch(
        listAllVehiclesInDbAsync({
          companyUuid: company.uuid,
          from: dates[0].toISOString(),
          to: dates[1].toISOString(),
        }),
      );
    },
    [dispatch, company],
  );

  // const reloadListWithEndDatePeriod = useCallback(
  //   (date: Date) => {
  //     if (!company) return;
  //     void dispatch(
  //       listAllVehiclesInDbAsync({
  //         companyUuid: company.uuid,
  //         to: date.toISOString(),
  //       }),
  //     );
  //   },
  //   [dispatch, company],
  // );

  return (
    <>
      <AddVehicleModal
        visible={isModalVisible}
        onCloseModal={() => {
          setIsModalVisible(false);
        }}
      />
      <AddMultipleVehiclesRequestConnectionModal
        visible={isRequestConnectionModalVisible}
        onCloseModal={() => setIsRequestConnectionModalVisible(false)}
      />
      <SharedInterfaceVehicles
        hasAdvancedAnalytics={company?.hasAdvancedAnalytics}
        cta={!canOrderVehicles ? undefined : cta}
        cta3={canRequestConnection ? cta3 : undefined}
        cta2={!canOrderVehicles ? undefined : cta2}
        sidePanelAction={sidePanelAction}
        reloadListWithDateFilters={reloadListWithDateFilters}
        //  reloadListWithEndDatePeriod={reloadListWithEndDatePeriod}
        sidePanel={
          <VehicleSidePanel
            canUpdateDepartment={!isUserManager}
            canUpdateAnalyticCode
            canUpdateNoteTaking
            canUpdateType
            canDeactivate
            canRequestConnection={canRequestConnection}
            hasAdvancedAnalytics={company?.hasAdvancedAnalytics}
          />
        }
        otherSupportPhones={otherSupportPhones}
      />
    </>
  );
};
